// Feature: M004-F0753 (Bookkeeping page Money owed block; design docs/design/M004-F0753-Bookkeeping-Links.md).
// Pure rules for the block. Field names are the Ledger's bookkeepingSummary wire shape at Ledger f6c66927
// (src/accounting/bookkeepingSummary.js buildBookkeepingSummary, returned by bookingKeeperService.summary as `balance_sheet`):
//   balance_sheet.assets.receivables.total               the receivable (issued invoices not yet settled), in POUNDS
//   balance_sheet.liabilities.customer_credit.total      unapplied credit across live standalone credit notes, in POUNDS
// The client never recomputes these figures; it only formats them and compares them with the list totals (design section 1.6, 3).
import { canViewInvoices } from '../invoices/invoiceModel';
import { canOpenCreditNotes, unusedTotalMinor, CREDIT_NOTE_LIST_CAP } from './creditNoteModel';

const asMinor = (pounds) => (typeof pounds === 'number' && Number.isFinite(pounds) ? Math.round(pounds * 100) : null);

// The two figures as the Ledger served them, in minor units. A figure the summary does not carry is null (never rendered, never guessed).
export function moneyFigures(summary) {
  const sheet = summary && typeof summary === 'object' ? summary.balance_sheet : null;
  const income = asMinor(sheet?.assets?.receivables?.total);
  const credit = asMinor(sheet?.liabilities?.customer_credit?.total);
  const customers = summary?.income?.customers;
  return {
    income,
    credit,
    available: income !== null || credit !== null,
    // "No invoices or credit notes yet": both figures are served and zero, and the Ledger's own per-customer income view is empty.
    empty: income === 0 && credit === 0 && Array.isArray(customers) && customers.length === 0,
  };
}

export const listLinks = (orgId, permissions) => ({
  invoices: canViewInvoices(permissions) ? `/app/ledger?tab=invoices&org=${encodeURIComponent(orgId)}` : null,
  creditNotes: canOpenCreditNotes(permissions) ? `/app/ledger?tab=credit-notes&org=${encodeURIComponent(orgId)}` : null,
});

// Invoice list outstanding: amount_due_minor (the Ledger's total less paid, credited and allocated credit) of every row that is not void.
export const outstandingMinor = (rows) => (Array.isArray(rows) ? rows : [])
  .filter((row) => row?.status !== 'void')
  .reduce((sum, row) => sum + Math.max(0, Number(row.amount_due_minor || 0)), 0);

export const creditListIncomplete = (rows) => (Array.isArray(rows) ? rows.length : 0) >= CREDIT_NOTE_LIST_CAP;

// Compare the displayed figures with the list totals. `lists` = { invoices: rows | null, creditNotes: rows | null, invoicesComplete, creditNotesComplete };
// null (not loaded, not permitted, incomplete) means that figure is not compared. Returns { state: 'match' | 'mismatch' | 'not_compared', mismatched: [] }.
export function reconcile(figures, lists) {
  const checks = [];
  if (figures.income !== null && Array.isArray(lists?.invoices) && lists.invoicesComplete) checks.push(['income', figures.income === outstandingMinor(lists.invoices)]);
  if (figures.credit !== null && Array.isArray(lists?.creditNotes) && lists.creditNotesComplete) checks.push(['credit', figures.credit === unusedTotalMinor(lists.creditNotes)]);
  if (checks.length === 0) return { state: 'not_compared', mismatched: [] };
  const mismatched = checks.filter(([, ok]) => !ok).map(([name]) => name);
  return { state: mismatched.length ? 'mismatch' : 'match', mismatched };
}
